import React, { useContext, useEffect } from 'react';
import { Button, DropdownToggleIcon } from '@sentara-health/optimahealth_web';
import { useTranslation } from 'react-i18next';
import {
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
  domainForSegment,
  getAnalyticsBaseConfig,
} from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { GlobalContext } from 'context';

type LanguageDropdownProps = {
  // optional className wrapper if a caller wants to pass layout classes
  className?: string;
};

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  className = '',
}) => {
  const { i18n } = useTranslation();
  const { logging } = useContext(GlobalContext);
  const currentLang = i18n?.language === 'en-US' ? 'en-US' : 'es';
  const { contentListingItemClicked } = useAdobeAnalytics();

  const getDocumentLang = (language: string) =>
    language.toLowerCase().startsWith('en') ? 'en' : 'es';

  useEffect(() => {
    const savedLang = localStorage.getItem('userLanguagePreference');
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = getDocumentLang(i18n.language);
  }, [i18n.language]);

  const trackLanguageSelection = (
    contentId: number,
    contentTitle: string,
    previousLanguage: string,
    newLanguage: string
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: contentId as unknown as string,
              contentTitle,
              itemPosition: 1,
              ...({ previousLanguage, newLanguage } as Record<string, string>),
            },
          ],
        },
      },
    });
  };

  const changeToEnglish = () => {
    const isSameLanguage = currentLang === 'en-US';
    localStorage.setItem('userLanguagePreference', 'en-US');
    i18n.changeLanguage('en-US');
    if (!isSameLanguage) {
      trackLanguageSelection(
        402,
        'Language_english_click',
        'spanish',
        'english'
      );
    }

    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig({
        languageSelected: 'English',
      }),
      message: ANALYTICS_MESSAGES.LANGUAGE_OPTION_SELECTED,
      screenName: SCREEN_NAMES.HEADER,
    });
  };

  const changeToSpanish = () => {
    const isSameLanguage = currentLang === 'es';
    localStorage.setItem('userLanguagePreference', 'es');
    i18n.changeLanguage('es');
    if (!isSameLanguage) {
      trackLanguageSelection(
        401,
        'Language_spanish_click',
        'english',
        'spanish'
      );
    }

    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig({
        languageSelected: 'Spanish',
      }),
      message: ANALYTICS_MESSAGES.LANGUAGE_OPTION_SELECTED,
      screenName: SCREEN_NAMES.HEADER,
    });
  };

  return (
    <div className={className}>
      <div className="d-flex align-items-center">
        <Button
          iconRight={<DropdownToggleIcon />}
          dataTestId="languageButton"
          type="button"
          data-bs-toggle="dropdown"
          variant="dropdown-item"
        >
          <span className="text-truncate">
            {currentLang === 'en-US' ? 'English' : 'Español'}
          </span>
        </Button>

        <ul
          className="dropdown-menu dropdown-menu-custom dropdown-menu-end"
          aria-labelledby="btnGroupDrop1"
        >
          <li>
            <Button
              dataTestId="englishButton"
              variant="dropdown-item"
              onClick={changeToEnglish}
            >
              English
            </Button>
          </li>
          <li>
            <Button
              dataTestId="spanishButton"
              variant="dropdown-item"
              onClick={changeToSpanish}
            >
              Español
            </Button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default LanguageDropdown;
